// @flow
import React, { type Element } from 'react';
import Loader from 'angieslist-app-platform/lib/browser/Loader';
import { compose, pure } from 'recompose';
import { reduxForm, Form } from 'redux-form';
import { analytics, Track, validation } from 'angieslist-app-platform';
import { Alert, Col, ReduxForm, Row } from 'angieslist-react-components';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';
import { defaultPageProps } from '../../../../app/browser/analyticsDefaultProperties';
import { itemClicked } from '../../../../app/browser/analyticsHelpers';
import profileActions from '../../common/profileActions';
import serviceProviderActions from '../../../../app/common/serviceProvider/serviceProviderActions';
import type { GetServiceProviderState } from '../../../../app/common/serviceProvider/serviceProviderReducer';
import type { ProfileReducerState } from '../../common/profileReducer';
import RequiredField from '../../../common/requiredField';
import BusinessHighlightCheckboxes from './BusinessHighlightCheckboxes';

import './About.scss';

type State = {
  submitSuccess: boolean,
};

type Props = {
  handleSubmit: any,
  serviceProviderId: number,
  serviceProvider: GetServiceProviderState,
  serviceProviderProfile: ProfileReducerState,
  getAllBusinessHighlights: typeof profileActions.getAllBusinessHighlights,
  getServiceProvider: typeof serviceProviderActions.getServiceProvider,
  saveAbout: typeof profileActions.saveAbout,
};

const OLDEST_YEAR_IN_BUSINESS = 1800;

const validationRules = {
  inBusinessSince: {
    required: false,
    pattern: 'digits',
    max: new Date().getFullYear(),
    min: OLDEST_YEAR_IN_BUSINESS,
    msg: 'Please enter a valid year (YYYY)',
  },
  description: {
    required: true,
    maxLength: 2000,
  },
};

export class AboutComponent extends React.Component<Props, State> {
  state: State = {
    submitSuccess: false,
  };

  componentDidMount() {
    const {
      getServiceProvider,
      serviceProviderId,
      getAllBusinessHighlights,
    } = this.props;
    getAllBusinessHighlights();
    getServiceProvider(serviceProviderId);
  }

  setRef: ((formRef: any) => void) = (formRef: Object) => {
    this.formRef = formRef;
  }

  formRef: Object;

  submitAbout: ((data: any) => void) = (data) => {
    const {
      saveAbout,
      serviceProviderId,
    } = this.props;
    const selectedHighlights = Object.entries(this.formRef.state.activeHighlights)
      .filter(([key, value]) => value === true);
    const businessHighlights = selectedHighlights.map(bh => ({ name: bh[0] }));
    saveAbout(
      serviceProviderId, data.uuid, data.inBusinessSince,
      data.description, businessHighlights,
    );
  }

  props: Props;

  render(): Element<"div"> {
    const {
      handleSubmit,
      serviceProvider,
      serviceProviderProfile: {
        getAllBusinessHighlights,
        currentProfileUAPI,
      },

    } = this.props;

    const allBusinessHighlights = get(getAllBusinessHighlights, 'response.businessHighlights', []);

    const currentBusinessHighlights = get(currentProfileUAPI, 'response.businessInfo.businessHighlights', []);

    const showAlert = get(this.state, 'submitSuccess', false);

    const isLoading = isEmpty(getAllBusinessHighlights.response)
       || getAllBusinessHighlights.isLoading
       || serviceProvider.isLoading
       || isEmpty(serviceProvider.response);

    return (
      <div id="About-Component-Container">
        <div className="page-name">About</div>
        <Form
          onSubmit={handleSubmit(data => this.submitAbout(data))}
          name="aboutForm"
          id="about-form"
        >
          <Row>
            <Col xs={12}>
              <div className="description">
                <p className="business-description-title">Business description</p>
                <RequiredField class="margin-about-page" />
              </div>
              <p>
                Tell potential customers what makes you stand out.
                Include details like specific services offered, info about your team’s
                expertise, and how you guarantee quality work.&nbsp;
                <a
                  id="intercom-business-description-article"
                  href="https://intercom.help/angi/en/articles/7030931-how-to-customize-your-business-description"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#02819E', fontWeight: 'bold' }}
                >
                  See examples.
                </a>
              </p>
              <Track>
                <ReduxForm.Input
                  name="description"
                  id="edit-business-details--business-description"
                  type="textarea"
                  placeholder="Type your description"
                />
              </Track>
              <p id="max-characters">
                2000 characters maximum
              </p>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <Track>
                <ReduxForm.Input
                  name="inBusinessSince"
                  label="In business since"
                  id="edit-business-details--in-business-since"
                  type="text"
                  placeholder="Year"
                />
              </Track>
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={7} sm={12}>
              <p className="business-highlights-title">Business highlights</p>
              <If condition={showAlert && !isLoading}>
                <Alert
                  bsStyle="success"
                  className="text-center"
                >
                  Business highlights updated successfully.
                  Please allow up to 24 hours to appear on your profile.
                </Alert>
              </If>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <p>
                Select any that apply to your business:
              </p>
            </Col>
          </Row>
          <Row>
            <Col>
              <Loader
                loading={isLoading}
                element={
                  <div className="text-center" style={{ marginTop: '1em' }}>
                    <i className="icon icon-load icon-spin icon-3x text-primary" />
                  </div>}
              >
                <BusinessHighlightCheckboxes
                  businessHighlights={currentBusinessHighlights}
                  allBusinessHighlights={allBusinessHighlights}
                  setRef={formRef => this.setRef(formRef)}
                />
              </Loader>
            </Col>
          </Row>
          <br />
        </Form>
      </div>
    );
  }
}

const aboutAnalytics = {
  page: {
    name: 'SP : My Business : About',
    properties: {
      ...defaultPageProps,
    },
  },
  elements: {
    'edit-business-details--business-description': itemClicked('SP : My Business : About', 'My Business : Description'),
    'edit-business-details--in-business-since': itemClicked('SP : My Business : About', 'My Business : In Business Since'),
  },
};

export default (hot(module)(compose(
  reduxForm({
    form: 'aboutForm',
    validate: values => validation.validate(values, validationRules).invalidAttrs,
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: false,
  }),
  connect(state => ({
    serviceProvider: state.serviceProviderProfile.currentProfileUAPI,
    serviceProviderProfile: state.serviceProviderProfile,
  }), {
    ...serviceProviderActions,
    ...profileActions,
  }),
  analytics({
    ruleset: aboutAnalytics,
  }),
  pure,
)(AboutComponent)) : any);



// WEBPACK FOOTER //
// ./src/modules/profile/browser/components/About.js